import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import cx from 'classnames';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Devices } from 'assets/icons/Devices.svg';
import { ReactComponent as Refresh } from 'assets/icons/Refresh.svg';
import Spinner from 'components/Spinner/Spinner';
import { isLoginSecretStorageUnlockActive } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useHasSecurityCode from 'contexts/MatrixDeviceContext/useHasSecurityCode';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import useToast from 'contexts/ToastProvider/useToast';
import ChooseVerificationMethodModal from 'modules/EndToEndEncryption/ChooseVerificationMethodModal/ChooseVerificationMethodModal';

const WaitingForSasVerificationModal = create(() => {
  const modal = useModal(WaitingForSasVerificationModal);
  const { t } = useTranslation();
  const chooseVerificationMethodModal = useModal(ChooseVerificationMethodModal);
  const { pushToast } = useToast();
  const {
    beginWaitingDeviceFlow,
    cancelVerification,
    sasDecimalNumbers,
    isRequesting,
    errorKey,
    setIsRequesting,
    setSasDecimalNumbers,
    setHasChosenSasVerification,
  } = useMatrixDeviceVerificationContext();

  const { hasSecurityCode } = useHasSecurityCode();

  // Keep a stable ref so the mount effect always calls the latest version without
  // re-triggering when beginWaitingDeviceFlow's identity changes (state updates inside
  // the flow reset deps, which would cause an infinite retry loop).
  const beginWaitingDeviceFlowRef = useRef(beginWaitingDeviceFlow);
  beginWaitingDeviceFlowRef.current = beginWaitingDeviceFlow;

  useEffect(() => {
    beginWaitingDeviceFlowRef.current();
  }, []);

  const handleRetry = async () => {
    pushToast({
      variant: 'info',
      title: t('verification.resending_verification_request'),
      autoDismiss: true,
    });
    setSasDecimalNumbers(null);
    cancelVerification(false, false);
    setIsRequesting(true);
    await new Promise((resolve) => setTimeout(resolve, 100));
    await beginWaitingDeviceFlow();
  };

  const handleChangeMethod = () => {
    cancelVerification();
    setHasChosenSasVerification(false);
    modal.remove();
    chooseVerificationMethodModal.show({
      isLoginUnlock: isLoginSecretStorageUnlockActive(),
    });
  };

  return (
    <Popup isVisible>
      <Popup.FullScreenContainer className="relative md:min-w-[512px] !bg-gray-10 border border-gray-30 rounded-2xl">
        <Popup.ScrollContainer className="flex flex-col items-center px-6 py-12 gap-6 font-poppins">
          <div className="flex flex-col items-center gap-8 w-full max-w-[464px]">
            <div className="shadow-icon rounded-3xl p-2 bg-white/40 backdrop-blur-[20px]">
              <div className="bg-tertiary-10 rounded-2xl p-4">
                <Devices className="h-8 w-8 shrink-0 stroke-2 stroke-tertiary-50" />
              </div>
            </div>
            <div className="flex flex-col gap-2 items-center text-center">
              <h2 className="font-semibold text-xl text-black leading-tight">
                {t('verification.verify_your_new_device')}
              </h2>
              <p className="text-base text-black">{t('verification.verify_new_device_subtitle')}</p>
            </div>
          </div>
          <div className="w-full max-w-[464px] bg-white border border-gray-30 rounded-2xl p-4 flex flex-col gap-2 items-center justify-center min-h-[120px]">
            {sasDecimalNumbers ? (
              <>
                <p className="text-4xl font-bold tracking-[4px] text-black">{sasDecimalNumbers}</p>
                <p className="text-sm text-black">{t('verification.check_codes_match_on_both_devices')}</p>
              </>
            ) : (
              <>
                <Spinner size="xs" variant="gray80" />
                <p className="text-sm text-gray-80">{t('verification.waiting_for_verification')}</p>
              </>
            )}
          </div>
          {errorKey && (
            <div className="w-full max-w-[464px] rounded-lg border border-danger-40 bg-danger-10 p-3 text-sm text-danger-70">
              {t(errorKey as string)}
            </div>
          )}
          <Button
            variant="secondaryTertiary"
            iconLeft={{
              icon: Refresh,
              className: cx('stroke-current shrink-0', { 'animate-spin-fast': isRequesting }),
            }}
            className="w-full max-w-[464px] rounded-2xl !bg-gray-10 hover:!bg-white"
            onClick={handleRetry}
            disabled={isRequesting}
          >
            {t('resend')}
          </Button>
          {hasSecurityCode && (
            <button
              type="button"
              className="text-primary-50 text-sm underline font-medium hover:text-primary-60 leading-5"
              onClick={handleChangeMethod}
            >
              {t('verification.dont_have_access_to_verified_device')}
            </button>
          )}
        </Popup.ScrollContainer>
      </Popup.FullScreenContainer>
    </Popup>
  );
});

export default WaitingForSasVerificationModal;
